import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import IssuerVM from './IssuerVM';
import { observer } from 'mobx-react';
import { Misc, ModalUtils, Validation } from "@singularsystems/neo-core";
import { Catalogue } from '@singularsystems/neo-react-services';
import BankDetails from "../Components/BankDetails";
import AssetClassCatalogueEntry from "../Catalogue/GenericComponents/AssetClassCatalogueEntry";
import IssuerPagedLookup from "../../Models/Issuers/Lookups/IssuerPagedLookup";
import IssuerTypeCatalogueEntry from "../Catalogue/General/IssuerTypeCatalogueEntry";
import * as Roles from "../../Models/Security/Roles"
import InternalRatingCatalogueEntry from "../Catalogue/GenericComponents/InternalRatingCatalogueEntry";
import IntraGroupCatalogueEntry from "../Catalogue/GenericComponents/IntraGroupCatalogueEntry";
import NotesGrid from '../Components/NotesGrid';
import SectorCatalogueEntry from '../Catalogue/Reporting/SectorsCatalogueEntry';
import AuthoriseButton from '../../Components/AuthoriseButton';
import AuthorisationComponents from '../../Components/AuthorisationComponents';
import ContactAutoCompleteDropdown from '../Contacts/Components/ContactAutoCompleteDropdown';
import Issuer from '../../Models/Issuers/Issuer';
import IssuerInvestmentsView from '../../../Domain/Views/IssuerInvestments/IssuerInvestmentView';
import ContactTypeCatalogueEntry from '../Catalogue/General/ContactTypeCatalogueEntry';
import { isHidden } from '../../Models/Config/FieldConfig/FieldSetting.enum';
import SARSNatureOfPersonCatalogueEntry from '../Catalogue/General/SARSNatureOfPersonCatalogueEntry';

class IssuerViewParams {
    public issuerId = {};
}

@observer
export default class IssuerView extends Views.ViewBase<IssuerVM, IssuerViewParams> {

    public static params = new IssuerViewParams();

    constructor(props: unknown) {
        super("Issuer", IssuerVM, props);
    }

    protected viewParamsUpdated(): void {
        this.viewModel.onIssuerParamsChanged(this.viewParams.issuerId);
    }

    public render() {
        var viewModel = this.viewModel;
        var canEdit = this.viewModel.authorisationService.hasRole(Roles.Issuer.Edit);
        const fieldSettings = viewModel.dataCache.fieldConfig.issuerFields;

        return (
            <div className="mt-3 mb-3">
                {!viewModel.editingIssuer &&
                    <Neo.Card title="Find Issuer">
                        <Neo.Form onSubmit={() => viewModel.autoRunner?.fireImmediate()} showSummaryModal>
                            <div style={{ textAlign: "right" }} className="mb-2">
                                <Neo.Button onClick={() => this.viewParams.issuerId.value = "new"} hidden={!canEdit}>Add Issuer</Neo.Button>

                            </div>
                            <div className="row">
                                <div className="col-lg-4">
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.issuerId} label="Issuer Id" />
                                </div>
                                <div className="col-lg-4">
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.issuerName} />
                                </div>
                                <div className="col-lg-4">
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.showUnauthorised} />
                                </div>
                            </div>
                            <Neo.Pager pageManager={viewModel.pageManager} pageControls="bottom" >
                                <NeoGrid.Grid<IssuerPagedLookup>>
                                    {(item, meta) => (
                                        <NeoGrid.Row>
                                            <NeoGrid.Column display={meta.issuerId} label="Issuer Id" width="15%" />
                                            <NeoGrid.Column display={meta.issuerName} width="35%" />
                                            <NeoGrid.Column display={meta.legalEntityTypeName} width="35%" />
                                            <NeoGrid.Column display={meta.isAuthorised} width="15%" />
                                            <NeoGrid.ButtonColumn editButton={{ onClick: () => this.viewParams.issuerId.value = item.issuerId }} />
                                        </NeoGrid.Row>
                                    )}
                                </NeoGrid.Grid>
                            </Neo.Pager>
                        </Neo.Form>
                    </Neo.Card>
                }
                {viewModel.editingIssuer &&
                    <Neo.Card title="Issuer Details">
                        <div>
                            <Neo.Form
                                onSubmit={() => viewModel.saveIssuer()} validationDisplayMode={Validation.DisplayMode.Always}
                                model={viewModel.editingIssuer}
                                showSummaryModal
                                allReadOnly={!canEdit}>
                                {(issuer, meta) => (
                                    <div>
                                        <div className="d-flex justify-content-between mb-4">
                                            <Neo.Button icon="arrow-left" variant="light" onClick={() => this.viewParams.issuerId.reset()}>Back</Neo.Button>
                                            <div>
                                                <Neo.Button icon="university" className="me-2" variant="light" hidden={!viewModel.editingIssuer!.issuerId} onClick={() => this.confirmSaveAndRedirect(issuer)}>View Investments</Neo.Button>
                                                <Neo.Button
                                                    className="min-width me-2"
                                                    tooltip={issuer.isPendingDelete ? "Stop this issuer from being removed when authorised." : "Mark this issuer to be removed when authorised."}
                                                    icon={issuer.isPendingDelete ? "trash-restore" : "trash"}
                                                    variant="danger"
                                                    hidden={!issuer.issuerId}
                                                    onClick={() => this.viewModel.setIssuerPendingDelete(issuer)} />
                                                <AuthoriseButton
                                                    className="me-2"
                                                    viewModel={viewModel.authorisationVM}
                                                    currentRecord={viewModel.editingIssuer!}
                                                    preAuthoriseRoleName={Roles.Issuer.PreAuthorise}
                                                    authoriseRoleName={Roles.Issuer.Authorise}
                                                    onDelete={() => {
                                                        this.viewModel.pageManager.refreshData(true);
                                                        this.viewParams.issuerId.reset();
                                                    }} />
                                                <Neo.Button isSubmit icon="save" pulse={viewModel.editingIssuer!.isDirty} disabled={!viewModel.editingIssuer!.isDirty} hidden={!canEdit}>Save</Neo.Button>
                                            </div>
                                        </div>

                                        <Neo.GridLayout lg={2}>
                                            <div>
                                                <Neo.Card title="Issuer Details">
                                                    <Neo.FormGroupInline bind={meta.issuerId} label="Ref No." readOnly={true} />
                                                    <Catalogue.FormGroupInline bind={meta.issuerTypeId} catalogueType={IssuerTypeCatalogueEntry} />
                                                    <Neo.FormGroupInline bind={meta.legalEntityTypeId} select={{ itemSource: viewModel.dataCache.legalEntityTypes }} />
                                                    <Neo.FormGroupInline bind={meta.issuerName} />
                                                    <Neo.FormGroupInline bind={meta.registrationNumber} hidden={isHidden(fieldSettings.registrationNumber)} />
                                                    <Neo.FormGroupInline bind={meta.cisNumbers} label='CIS Numbers' hidden={isHidden(fieldSettings.cisNumbers)} />
                                                    <Neo.FormGroupInline bind={meta.taxRefNo} hidden={isHidden(fieldSettings.taxRefNo)} />
                                                    <Neo.FormGroupInline bind={meta.postalAddress} input={{ rows: 3 }} hidden={isHidden(fieldSettings.postalAddress)} />
                                                    <Neo.FormGroupInline bind={meta.physicalAddress} input={{ rows: 3 }} hidden={isHidden(fieldSettings.physicalAddress)} />
                                                    <Catalogue.FormGroupInline bind={meta.internalRatingId} catalogueType={InternalRatingCatalogueEntry} hidden={isHidden(fieldSettings.internalRatingId)} />
                                                    <Catalogue.FormGroupInline bind={meta.intraGroupId} catalogueType={IntraGroupCatalogueEntry} hidden={isHidden(fieldSettings.intraGroupId)} />
                                                    <Catalogue.FormGroupInline bind={meta.sectorId} catalogueType={SectorCatalogueEntry} hidden={isHidden(fieldSettings.sectorId)} />
                                                    <Catalogue.FormGroupInline bind={meta.sarsNatureOfPersonId} catalogueType={SARSNatureOfPersonCatalogueEntry} hidden={isHidden(fieldSettings.sarsNatureOfPersonId)} />
                                                </Neo.Card>
                                                <Neo.Card title="Bank Details">
                                                    <div>
                                                        <BankDetails bankDetail={issuer.bankDetails} />
                                                    </div>
                                                </Neo.Card>
                                                <Neo.Card title="Contacts">
                                                    <Neo.FormGroupInline>
                                                        <ContactAutoCompleteDropdown
                                                            bind={issuer.meta.authorisedPersonContactId}
                                                            bindDisplay={issuer.meta.authorisedContactName}
                                                            parentName="issuer"
                                                        />
                                                    </Neo.FormGroupInline>
                                                    <Neo.FormGroupInline>
                                                        <ContactAutoCompleteDropdown
                                                            bind={issuer.meta.referralContactId}
                                                            bindDisplay={issuer.meta.referralContactName}
                                                            parentName="issuer"
                                                        />
                                                    </Neo.FormGroupInline>
                                                    <NeoGrid.Grid items={issuer.contacts} showAddButton>
                                                        {(item) => (
                                                            <NeoGrid.Row>
                                                                <NeoGrid.Column bindContext={item.meta.contactId}>
                                                                    <ContactAutoCompleteDropdown
                                                                        bind={item.meta.contactId}
                                                                        bindDisplay={item.meta.contactName}
                                                                        parentName="issuer"
                                                                    />
                                                                </NeoGrid.Column>
                                                                <Catalogue.Column bind={item.meta.contactTypeId} catalogueType={ContactTypeCatalogueEntry} />
                                                                <NeoGrid.ButtonColumn showDelete />
                                                            </NeoGrid.Row>
                                                        )}
                                                    </NeoGrid.Grid>
                                                </Neo.Card>
                                            </div>
                                            <div>
                                                <Neo.Card title="Other Details">
                                                    <Catalogue.FormGroupInline bind={meta.assetClassId} catalogueType={AssetClassCatalogueEntry} hidden={isHidden(fieldSettings.assetClassId)} />
                                                    <Neo.FormGroupInline bind={meta.countryId} select={{ itemSource: viewModel.dataCache.countries }} hidden={isHidden(fieldSettings.countryId)} />
                                                    <Neo.FormGroupInline bind={meta.withholdingAgentReferenceNumber} hidden={isHidden(fieldSettings.withholdingAgentReferenceNumber)} />
                                                    <Neo.FormGroupInline bind={meta.ledgerAccount} hidden={isHidden(fieldSettings.ledgerAccount)} />
                                                    <Neo.FormGroupInline bind={meta.ficaDate} hidden={isHidden(fieldSettings.ficaDate)} />
                                                    <Neo.FormGroupInline bind={meta.sanctionScreeningPerformed} label='Sanctioned screening performed?' hidden={isHidden(fieldSettings.sactionedScreeningPerformed)} />
                                                    <Neo.FormGroupInline bind={meta.isRestricted} label='Restricted from trading?' hidden={isHidden(fieldSettings.isRestricted)} />
                                                    <Neo.FormGroupInline bind={meta.lossGivenDefault} numProps={{ formatString: "##0.0000" }} appendText={'%'} hidden={isHidden(fieldSettings.lossGivenDefault)} />
                                                </Neo.Card>
                                                <Neo.Card title="Notes">
                                                    <NotesGrid parentModel={viewModel.editingIssuer!} viewModel={viewModel.noteVM} />
                                                </Neo.Card>
                                            </div>
                                        </Neo.GridLayout>
                                    </div>
                                )}
                            </Neo.Form>
                        </div>
                    </Neo.Card>
                }

                <AuthorisationComponents viewModel={this.viewModel.authorisationVM} />
            </div>
        );
    }

    private async confirmSaveAndRedirect(issuer: Issuer) {
        if (issuer.readyToSave && await ModalUtils.showYesNoDismissible('save', `Would you like to save the changes that were made to this issuer?`) === Misc.ModalResult.Yes) {
            await this.viewModel.saveIssuer();
        }
        IssuerVM.lastIssuer = issuer;
        this.navigation.navigateToView(IssuerInvestmentsView, { issuerId: issuer.issuerId });
    }

    public onLeave(): string | undefined {
        if (this.viewModel.editingIssuer?.isDirty) {
            return "Are you sure you want to leave this page without saving?";
        }
        return undefined;
    }
}